/** Shared flood exposure labels and styling for map search + directions. */

import type { FillLayerSpecification } from 'mapbox-gl';

export type FloodRiskLevel = 'low' | 'medium' | 'high' | 'extreme';

/** Native vector tile zoom range (CHIRPS ~5.5 km grid). */
export const FLOOD_TILE_MIN_ZOOM = 6;
export const FLOOD_TILE_MAX_ZOOM = 13;
/** Map layer visibility — overzooms native z13 tiles through street-level zoom. */
export const FLOOD_LAYER_MAX_ZOOM = 22;

/** Calendar years with per-year flood exposure period tiles under flood_exposure/periods/{year}/. */
export const FLOOD_EXPOSURE_AVAILABLE_YEARS = [2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026] as const;

export const FLOOD_EXPOSURE_GRID_LEGEND: ReadonlyArray<{
  level: FloodRiskLevel;
  label: string;
  color: string;
}> = [
  { level: 'low', label: 'Low', color: 'rgba(34, 197, 94, 0.55)' },
  { level: 'medium', label: 'Medium', color: 'rgba(234, 179, 8, 0.65)' },
  { level: 'high', label: 'High', color: 'rgba(249, 115, 22, 0.75)' },
  { level: 'extreme', label: 'Extreme', color: 'rgba(239, 68, 68, 0.85)' },
];

export const FLOOD_FILL_PAINT: NonNullable<FillLayerSpecification['paint']> = {
  'fill-color': [
    'match',
    ['get', 'flood_risk_level'],
    'low',
    'rgba(34, 197, 94, 0.12)',
    'medium',
    'rgba(234, 179, 8, 0.22)',
    'high',
    'rgba(249, 115, 22, 0.32)',
    'extreme',
    'rgba(239, 68, 68, 0.42)',
    'rgba(148, 163, 184, 0.08)',
  ],
  'fill-opacity': 0.85,
};

export interface FloodExposureSegment {
  way_id?: number | null;
  name?: string | null;
  flood_risk_level?: FloodRiskLevel | string | null;
  flood_prone?: boolean | null;
  length_meters?: number | null;
  avg_rainfall_mm?: number | null;
}

export interface FloodExposureSummary {
  max_risk_level?: FloodRiskLevel | string | null;
  flood_prone?: boolean;
  flood_prone_distance_meters?: number;
  total_distance_meters?: number;
  distance_by_risk?: Record<string, number>;
  high_risk_segments?: FloodExposureSegment[];
  data_source?: string;
  note?: string | null;
}

const RISK_LABEL: Record<string, string> = {
  low: 'Low flood exposure',
  medium: 'Medium flood exposure',
  high: 'High flood exposure',
  extreme: 'Extreme flood exposure',
};

const RISK_BADGE_CLASS: Record<string, string> = {
  low: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/30',
  medium: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30',
  high: 'text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/30',
  extreme: 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30',
};

export function floodRiskLabel(level?: string | null): string | null {
  if (!level) return null;
  return RISK_LABEL[level] ?? `${level} flood exposure`;
}

export function floodRiskBadgeClass(level?: string | null): string {
  if (!level) return 'text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700/60';
  return RISK_BADGE_CLASS[level] ?? RISK_BADGE_CLASS.medium;
}

export function formatFloodExposureSummary(summary: FloodExposureSummary | null | undefined): string | null {
  if (!summary?.max_risk_level) return null;
  const label = floodRiskLabel(summary.max_risk_level);
  if (!label) return null;
  if (summary.flood_prone && (summary.flood_prone_distance_meters ?? 0) > 0) {
    const km = ((summary.flood_prone_distance_meters ?? 0) / 1000).toFixed(1);
    return `${label} · ${km} km flood-prone along route`;
  }
  return `${label} along route`;
}

export const FLOOD_EXPOSURE_DISCLAIMER =
  'Shows typical rainy-season flood risk from long-term rainfall patterns. This is not a live flood alert.';

export const FLOOD_EXPOSURE_SUMMARY =
  'See which areas tend to flood during heavy rains, based on many years of rainfall history. Turn on the long-term average, or pick a specific year to compare. Helpful for planning trips — not for real-time warnings.';
